/**
 * Sync Inbox — единая модалка управляемой синхронизации с Jira.
 *
 * Два режима через переключатель сверху:
 *   ⇨ Push (Lens → Jira)
 *   ⇦ Pull (Jira → Lens)
 *
 * Каждая запись представления показывает свои счётчики (полей изменится),
 * можно выбрать чекбоксами что синхронизировать, опционально сузить набор
 * полей через глобальный фильтр. Drill-down в per-record diff — открывает
 * SyncRecordDrawer.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Segmented,
  Button,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Progress,
  Collapse,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  fetchSyncInbox,
  fetchLinksInbox,
  bulkJiraPush,
  bulkJiraPull,
  bulkLinksSync,
  bulkJiraSyncBoth,
  getJob,
} from './api.ts';
import { useJobs } from '../jobs/JobsContext.tsx';
import { SyncRecordDrawer } from './SyncRecordDrawer.tsx';
import { LinksRecordDrawer } from './LinksRecordDrawer.tsx';
import { JiraPullPanel } from './JiraPullPanel.tsx';
import type { SyncInboxItem, SyncInboxResponse, LinksInboxItem, LinksInboxResponse } from '@lens/shared';
import { radius, spacing } from '../../shared/theme/tokens.ts';

const { Text } = Typography;

interface SyncInboxModalProps {
  open: boolean;
  spaceId: string;
  viewId: string;
  viewName: string;
  /**
   * Если view содержит один itemType — его id и имя. Нужно для sub-flow
   * «Импорт новых из Jira по JQL» (он работает только для одного типа).
   * При null/multi-type кнопка импорта будет disabled с подсказкой.
   */
  singleTypeId: string | null;
  singleTypeName: string | null;
  /**
   * Быстрая синхронизация по выбранным строкам таблицы (todos #3): если задан
   * непустой массив — inbox показывает только эти записи, view-фильтры
   * игнорируются, импорт новых из Jira недоступен (нет смысла при
   * ограничении по itemId). Также отключается переключатель «только
   * actionable» — при ручном выборе хочется видеть всё, что выбрано.
   */
  restrictToItemIds?: string[] | null;
  onClose: () => void;
  onApplied: () => void;
}

type Direction = 'push' | 'pull';
type Kind = 'fields' | 'links' | 'both';
type Phase = 'loading' | 'ready' | 'starting' | 'running' | 'success' | 'error';

/** Строка объединённой таблицы для scope='both' — поля + связи по записи. */
interface BothRow {
  itemId: string;
  title: string;
  jiraIssueKey: string | null;
  fieldAction: SyncInboxItem['action'] | null;
  changedFieldCount: number;
  linkActionable: number;
  hasError: boolean;
}

const ACTION_LABELS: Record<SyncInboxItem['action'], { label: string; color: string }> = {
  create: { label: 'создать в Jira', color: 'blue' },
  update: { label: 'обновить', color: 'gold' },
  no_change: { label: 'без изменений', color: 'default' },
  unlinked: { label: 'не связано', color: 'default' },
};

export function SyncInboxModal({
  open,
  spaceId,
  viewId,
  viewName,
  singleTypeId,
  singleTypeName,
  restrictToItemIds,
  onClose,
  onApplied,
}: SyncInboxModalProps) {
  const restrictKey = restrictToItemIds && restrictToItemIds.length > 0
    ? [...restrictToItemIds].sort().join(',')
    : '';
  const isRestricted = restrictKey.length > 0;
  const { registerJob } = useJobs();
  const [direction, setDirection] = useState<Direction>('push');
  const [kind, setKind] = useState<Kind>('fields');
  const [phase, setPhase] = useState<Phase>('loading');
  const [inbox, setInbox] = useState<SyncInboxResponse | null>(null);
  const [linksInbox, setLinksInbox] = useState<LinksInboxResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [includeFieldSlugs, setIncludeFieldSlugs] = useState<string[]>([]);
  const [includeLinkTypes, setIncludeLinkTypes] = useState<string[]>([]);
  const [showOnlyActionable, setShowOnlyActionable] = useState(true);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [drawerItemId, setDrawerItemId] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<{
    ok: number;
    errors: number;
    errorDetails: Array<{ itemId: string; title: string; jiraIssueKey: string | null; error: string }>;
  } | null>(null);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setPhase('loading');
      setInbox(null);
      setLinksInbox(null);
      setErrorMsg(null);
      setIncludeFieldSlugs([]);
      setIncludeLinkTypes([]);
      setSelectedItemIds([]);
      setDrawerItemId(null);
      setJobId(null);
      setResult(null);
      setProgress({ done: 0, total: 0 });
    }
  }, [open]);

  // Load inbox on open / direction / kind / filter change
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase('loading');
    setErrorMsg(null);
    setSelectedItemIds([]);

    const restrictIds = isRestricted ? restrictKey.split(',') : undefined;
    const loader = kind === 'fields'
      ? fetchSyncInbox(spaceId, viewId, direction, { includeFieldSlugs, itemIds: restrictIds }).then((res) => {
          if (cancelled) return;
          setInbox(res);
          setLinksInbox(null);
          const actionable = res.items.filter(
            (i) => (i.action === 'create' || i.action === 'update') && !i.errorMessage,
          );
          setSelectedItemIds(actionable.map((i) => i.itemId));
          setPhase('ready');
        })
      : kind === 'links'
      ? fetchLinksInbox(spaceId, viewId, direction, { includeLinkTypes, itemIds: restrictIds }).then((res) => {
          if (cancelled) return;
          setLinksInbox(res);
          setInbox(null);
          const actionable = res.items.filter((i) => i.actionableCount > 0 && !i.errorMessage);
          setSelectedItemIds(actionable.map((i) => i.itemId));
          setPhase('ready');
        })
      : // both — грузим оба inbox'а, выбираем объединение actionable-записей.
        Promise.all([
          fetchSyncInbox(spaceId, viewId, direction, { includeFieldSlugs, itemIds: restrictIds }),
          fetchLinksInbox(spaceId, viewId, direction, { includeLinkTypes, itemIds: restrictIds }),
        ]).then(([fieldsRes, linksRes]) => {
          if (cancelled) return;
          setInbox(fieldsRes);
          setLinksInbox(linksRes);
          const actionable = new Set<string>();
          for (const i of fieldsRes.items) {
            if ((i.action === 'create' || i.action === 'update') && !i.errorMessage) actionable.add(i.itemId);
          }
          for (const i of linksRes.items) {
            if (i.actionableCount > 0 && !i.errorMessage) actionable.add(i.itemId);
          }
          setSelectedItemIds([...actionable]);
          setPhase('ready');
        });

    loader.catch((err: unknown) => {
      if (cancelled) return;
      setErrorMsg(err instanceof Error ? err.message : 'Не удалось загрузить inbox');
      setPhase('error');
    });

    return () => { cancelled = true; };
  }, [open, spaceId, viewId, direction, kind, includeFieldSlugs, includeLinkTypes, restrictKey, isRestricted]);

  // Poll job
  useEffect(() => {
    if (!jobId || phase !== 'running') return;
    let active = true;
    const interval = setInterval(async () => {
      if (!active) return;
      try {
        const job = await getJob(jobId);
        if (!active) return;
        setProgress({ done: job.progress ?? 0, total: job.total ?? 0 });
        if (job.status === 'success') {
          const titleByItemId = new Map<string, { title: string; jiraIssueKey: string | null }>();
          for (const it of inbox?.items ?? []) {
            titleByItemId.set(it.itemId, { title: it.title, jiraIssueKey: it.jiraIssueKey });
          }
          for (const it of linksInbox?.items ?? []) {
            titleByItemId.set(it.itemId, { title: it.title, jiraIssueKey: it.jiraIssueKey });
          }
          type SubResult = {
            pushed?: number; pulled?: number; created?: number; errors?: number;
            results?: Array<{ itemId: string; jiraIssueKey?: string | null; error?: string; errors?: string[] }>;
          };
          const extract = (sub: SubResult | undefined) => {
            const details: Array<{ itemId: string; title: string; jiraIssueKey: string | null; error: string }> = [];
            for (const entry of sub?.results ?? []) {
              const meta = titleByItemId.get(entry.itemId) ?? { title: entry.itemId, jiraIssueKey: entry.jiraIssueKey ?? null };
              if (entry.error) details.push({ itemId: entry.itemId, title: meta.title, jiraIssueKey: meta.jiraIssueKey ?? entry.jiraIssueKey ?? null, error: entry.error });
              for (const e of entry.errors ?? []) {
                details.push({ itemId: entry.itemId, title: meta.title, jiraIssueKey: meta.jiraIssueKey ?? entry.jiraIssueKey ?? null, error: e });
              }
            }
            return { ok: sub?.pushed ?? sub?.pulled ?? sub?.created ?? 0, errors: sub?.errors ?? 0, details };
          };
          // both — результат содержит фазы { fields, links }; иначе плоский sub-result.
          const raw = (job.result ?? {}) as SubResult & { fields?: SubResult; links?: SubResult };
          const parts = kind === 'both' ? [extract(raw.fields), extract(raw.links)] : [extract(raw)];
          setResult({
            ok: parts.reduce((s, p) => s + p.ok, 0),
            errors: parts.reduce((s, p) => s + p.errors, 0),
            errorDetails: parts.flatMap((p) => p.details),
          });
          setPhase('success');
          onApplied();
          clearInterval(interval);
        } else if (job.status === 'error') {
          setErrorMsg(job.error ?? 'Ошибка выполнения');
          setPhase('error');
          clearInterval(interval);
        } else if (job.status === 'cancelled') {
          setPhase('ready');
          clearInterval(interval);
        }
      } catch {
        // ignore transient errors
      }
    }, 1500);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [jobId, phase, onApplied, inbox, linksInbox]);

  const fieldOptions = useMemo(
    () => (inbox?.availableFields ?? []).map((f) => ({ label: f.name, value: f.slug })),
    [inbox?.availableFields],
  );
  const linkTypeOptions = useMemo(
    () => (linksInbox?.availableLinkTypes ?? []).map((t) => ({ label: t, value: t })),
    [linksInbox?.availableLinkTypes],
  );

  // Объединённые строки для scope='both': по одной на запись, поля + связи.
  const bothRows = useMemo<BothRow[]>(() => {
    if (kind !== 'both') return [];
    const byId = new Map<string, BothRow>();
    for (const f of inbox?.items ?? []) {
      byId.set(f.itemId, {
        itemId: f.itemId,
        title: f.title,
        jiraIssueKey: f.jiraIssueKey,
        fieldAction: f.action,
        changedFieldCount: f.changedFieldCount,
        linkActionable: 0,
        hasError: !!f.errorMessage,
      });
    }
    for (const l of linksInbox?.items ?? []) {
      const row = byId.get(l.itemId);
      if (row) {
        row.linkActionable = l.actionableCount;
        row.hasError = row.hasError || !!l.errorMessage;
      } else {
        byId.set(l.itemId, {
          itemId: l.itemId,
          title: l.title,
          jiraIssueKey: l.jiraIssueKey,
          fieldAction: null,
          changedFieldCount: 0,
          linkActionable: l.actionableCount,
          hasError: !!l.errorMessage,
        });
      }
    }
    return [...byId.values()];
  }, [kind, inbox?.items, linksInbox?.items]);

  const bothColumns: TableColumnsType<BothRow> = [
    {
      title: 'Запись',
      key: 'title',
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.title}</div>
          {row.jiraIssueKey ? (
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.jiraIssueKey}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>— без Jira-ключа</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Поля',
      key: 'fields',
      width: 200,
      render: (_, row) => {
        if (!row.fieldAction || row.fieldAction === 'no_change' || row.fieldAction === 'unlinked') {
          return <Text type="secondary">—</Text>;
        }
        const meta = ACTION_LABELS[row.fieldAction];
        return (
          <Space size={6}>
            <Tag color={meta.color}>{meta.label}</Tag>
            {row.fieldAction === 'update' && row.changedFieldCount > 0 && <Text type="secondary">{row.changedFieldCount}</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Связи',
      key: 'links',
      width: 120,
      align: 'center',
      render: (_, row) => row.linkActionable > 0
        ? <Tag color={direction === 'push' ? 'blue' : 'purple'}>{row.linkActionable}</Tag>
        : <Text type="secondary">—</Text>,
    },
  ];

  const handleStart = async () => {
    if (selectedItemIds.length === 0) {
      void message.warning('Выберите записи для синхронизации');
      return;
    }
    setPhase('starting');
    setErrorMsg(null);
    setResult(null);
    setProgress({ done: 0, total: selectedItemIds.length });
    try {
      let res: { jobId: string };
      if (kind === 'fields') {
        const body = {
          itemIds: selectedItemIds,
          ...(includeFieldSlugs.length > 0 ? { includeFieldSlugs } : {}),
        };
        res = direction === 'push'
          ? await bulkJiraPush(spaceId, body)
          : await bulkJiraPull(spaceId, body);
      } else if (kind === 'links') {
        res = await bulkLinksSync(spaceId, {
          itemIds: selectedItemIds,
          direction,
          ...(includeLinkTypes.length > 0 ? { includeLinkTypes } : {}),
        });
      } else {
        // both — поля и связи одной джобой (bulk_jira_sync_both).
        res = await bulkJiraSyncBoth(spaceId, {
          itemIds: selectedItemIds,
          direction,
          ...(includeFieldSlugs.length > 0 ? { includeFieldSlugs } : {}),
          ...(includeLinkTypes.length > 0 ? { includeLinkTypes } : {}),
        });
      }
      setJobId(res.jobId);
      setPhase('running');
      // Регистрируем job в глобальном индикаторе — чтобы прогресс был виден
      // после «Продолжить в фоне» (закрытия модалки) и смены страницы.
      const dirLabel = direction === 'push' ? 'Синхронизация в Jira' : 'Импорт из Jira';
      const kindLabel = kind === 'links' ? 'связи' : kind === 'both' ? 'поля и связи' : 'поля';
      registerJob(res.jobId, `${dirLabel} (${kindLabel}) · ${viewName}`);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Не удалось запустить sync');
      setPhase('error');
    }
  };

  // ─── Links table columns ────────────────────────────────────────────
  const linksColumns: TableColumnsType<LinksInboxItem> = [
    {
      title: 'Запись',
      key: 'title',
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.title}</div>
          {row.jiraIssueKey ? (
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.jiraIssueKey}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>— без Jira-ключа</Text>
          )}
        </div>
      ),
    },
    {
      title: direction === 'push' ? 'К push' : 'К pull',
      dataIndex: 'actionableCount',
      key: 'actionable',
      width: 110,
      align: 'center',
      render: (n: number) => n > 0 ? <Tag color={direction === 'push' ? 'blue' : 'purple'}>{n}</Tag> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Уже синхронно',
      dataIndex: 'syncedCount',
      key: 'synced',
      width: 130,
      align: 'center',
      render: (n: number) => <Text type="secondary">{n}</Text>,
    },
    {
      title: 'Нельзя',
      dataIndex: 'unsyncableCount',
      key: 'unsyncable',
      width: 100,
      align: 'center',
      render: (n: number) => n > 0 ? <Tooltip title={direction === 'push' ? 'У целевой записи нет jiraIssueKey' : 'Другой конец не зеркалирован в Lens'}><Tag color="default">{n}</Tag></Tooltip> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Заметка',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (m: string | undefined) =>
        m ? <Text type="danger" style={{ fontSize: 12 }}>{m}</Text> : null,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, row) =>
        row.actionableCount === 0 && row.syncedCount === 0 ? null : (
          <Button size="small" onClick={() => setDrawerItemId(row.itemId)}>Превью</Button>
        ),
    },
  ];

  // Table columns adapt by direction
  const columns: TableColumnsType<SyncInboxItem> = [
    {
      title: 'Запись',
      dataIndex: 'title',
      key: 'title',
      render: (_: unknown, row: SyncInboxItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.title}</div>
          {row.jiraIssueKey ? (
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
              {row.jiraIssueKey}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>— без Jira-ключа</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (a: SyncInboxItem['action']) => {
        const meta = ACTION_LABELS[a];
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Полей изменится',
      dataIndex: 'changedFieldCount',
      key: 'changedFieldCount',
      width: 130,
      align: 'center',
      render: (n: number, row: SyncInboxItem) => {
        if (row.action === 'create') return <Text type="secondary">все</Text>;
        if (n === 0) return <Text type="secondary">0</Text>;
        return (
          <Tooltip title={row.changedFieldSlugs.join(', ')}>
            <b>{n}</b>
          </Tooltip>
        );
      },
    },
    {
      title: 'Заметка',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (m: string | undefined) =>
        m ? <Text type="danger" style={{ fontSize: 12 }}>{m}</Text> : null,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_: unknown, row: SyncInboxItem) =>
        row.action === 'no_change' || row.action === 'unlinked' ? null : (
          <Button size="small" onClick={() => setDrawerItemId(row.itemId)}>
            Превью
          </Button>
        ),
    },
  ];

  const isRunning = phase === 'running' || phase === 'starting';
  const isTerminal = phase === 'success' || phase === 'error';

  const showFields = kind === 'fields' || kind === 'both';
  const showLinks = kind === 'links' || kind === 'both';

  const fieldsStats = showFields && inbox ? (
    <Space size="large" wrap>
      {kind === 'both' && <Text strong>Поля:</Text>}
      <Text>
        <b>{inbox.totalItems}</b> записей в представлении
      </Text>
      {direction === 'push' && (
        <Text type="success">
          К создать: <b>{inbox.toCreate}</b>
        </Text>
      )}
      <Text style={{ color: 'var(--color-warning)' }}>
        К обновлению: <b>{inbox.toUpdate}</b>
      </Text>
      <Text type="secondary">
        Без изменений: <b>{inbox.noChange}</b>
      </Text>
      {direction === 'pull' && inbox.unlinked > 0 && (
        <Text type="secondary">
          Не связано с Jira: <b>{inbox.unlinked}</b>
        </Text>
      )}
      {inbox.errors > 0 && (
        <Text type="danger">
          С ошибками: <b>{inbox.errors}</b>
        </Text>
      )}
    </Space>
  ) : null;

  const linksStats = showLinks && linksInbox ? (
    <Space size="large" wrap>
      {kind === 'both' && <Text strong>Связи:</Text>}
      {kind !== 'both' && (
        <Text>
          <b>{linksInbox.totalItems}</b> записей в представлении
        </Text>
      )}
      <Text style={{ color: direction === 'push' ? 'var(--color-info)' : 'var(--color-primary)' }}>
        {direction === 'push' ? 'К push:' : 'К pull:'} <b>{linksInbox.totalActionable}</b> связей
      </Text>
      <Text type="secondary">
        Уже синхронно: <b>{linksInbox.totalSynced}</b>
      </Text>
      {linksInbox.totalUnsyncable > 0 && (
        <Tooltip title={direction === 'push' ? 'У target нет jiraIssueKey' : 'Другой конец не зеркалирован в Lens'}>
          <Text type="secondary">
            Нельзя: <b>{linksInbox.totalUnsyncable}</b>
          </Text>
        </Tooltip>
      )}
      {linksInbox.errors > 0 && (
        <Text type="danger">
          С ошибками: <b>{linksInbox.errors}</b>
        </Text>
      )}
    </Space>
  ) : null;

  const stats = (fieldsStats || linksStats) ? (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {fieldsStats}
      {linksStats}
    </Space>
  ) : null;

  return (
    <>
      <Modal
        title={
          isRestricted
            ? `Синхронизация с Jira — выбранные записи (${restrictKey.split(',').length})`
            : `Синхронизация с Jira — представление «${viewName}»`
        }
        open={open}
        onCancel={onClose}
        width={1100}
        maskClosable={false}
        destroyOnHidden
        footer={
          isTerminal ? (
            <Space>
              <Button onClick={onClose}>Закрыть</Button>
              {phase === 'success' && (
                <Button type="primary" onClick={() => {
                  setPhase('loading');
                  const restrictIds = isRestricted ? restrictKey.split(',') : undefined;
                  fetchSyncInbox(spaceId, viewId, direction, { includeFieldSlugs, itemIds: restrictIds }).then((r) => {
                    setInbox(r);
                    setPhase('ready');
                    setSelectedItemIds([]);
                  });
                }}>
                  Обновить inbox
                </Button>
              )}
            </Space>
          ) : isRunning ? (
            <Button onClick={onClose}>Свернуть в фон (прогресс — в индикаторе рельса)</Button>
          ) : (
            <Space>
              <Button onClick={onClose}>Закрыть</Button>
              {/* Кнопка «Сохранить как расписание» удалена в FR-12 unification.
                  Создавайте правило с шагом jira.push во вкладке «Автоматизации». */}
              {selectedItemIds.length > 0 && phase === 'ready' && (
                <Button
                  type="primary"
                  onClick={() => void handleStart()}
                  data-testid="sync-inbox-apply"
                >
                  {direction === 'push' ? '▶ Запустить push' : '▶ Запустить pull'} ({selectedItemIds.length})
                </Button>
              )}
            </Space>
          )
        }
      >
        {/* Outer kind switch + inner direction switch */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Segmented
            value={kind}
            onChange={(v) => setKind(v as Kind)}
            options={[
              { label: 'Поля', value: 'fields' },
              { label: '🔗 Связи', value: 'links' },
              { label: '⇄ Всё', value: 'both' },
            ]}
            disabled={isRunning}
          />
          <Segmented
            value={direction}
            onChange={(v) => setDirection(v as Direction)}
            options={[
              { label: '⇨ Push (Lens → Jira)', value: 'push' },
              { label: '⇦ Pull (Jira → Lens)', value: 'pull' },
            ]}
            disabled={isRunning}
          />
        </div>

        {/* Stats */}
        {stats && <div style={{ marginBottom: 12 }}>{stats}</div>}

        {/* Filter (fields or link types depending on kind) */}
        {phase === 'ready' && showFields && fieldOptions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Синхронизировать только поля:
            </Text>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Все поля из маппинга (по умолчанию)"
              value={includeFieldSlugs}
              onChange={setIncludeFieldSlugs}
              options={fieldOptions}
              optionFilterProp="label"
              data-testid="sync-inbox-field-filter"
            />
          </div>
        )}
        {phase === 'ready' && showLinks && linkTypeOptions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Синхронизировать только типы связей:
            </Text>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Все типы (по умолчанию)"
              value={includeLinkTypes}
              onChange={setIncludeLinkTypes}
              options={linkTypeOptions}
              optionFilterProp="label"
              data-testid="sync-inbox-linktype-filter"
            />
          </div>
        )}

        {/* Warnings */}
        {[
          ...(showFields ? inbox?.warnings ?? [] : []),
          ...(showLinks ? linksInbox?.warnings ?? [] : []),
        ].map((w, i) => (
          <div key={i} style={{ marginBottom: spacing.sm, padding: '6px 10px', background: 'var(--color-warning-bg)', border: `1px solid ${'var(--color-warning)'}`, borderRadius: radius.sm, fontSize: 12 }}>
            ⚠ {w}
          </div>
        ))}

        {/* Импорт новых задач из Jira — встроен прямо во вкладку Pull (единое
            окно): выбор сохранённого запроса, JQL, превью новых и импорт. Только
            для view-mode с одним типом записи; при ручном выборе строк или
            multi-type импорт новых недоступен. */}
        {phase === 'ready' && direction === 'pull' && !isRestricted && (
          <div style={{
            marginBottom: spacing.md,
            padding: '12px 14px',
            background: 'var(--color-info-bg)',
            border: `1px dashed ${'var(--color-info)'}`,
            borderRadius: radius.sm,
          }}>
            {singleTypeId ? (
              <JiraPullPanel
                active
                spaceId={spaceId}
                itemTypeId={singleTypeId}
                itemTypeName={singleTypeName ?? ''}
                autoSearch
                onImported={() => {
                  // После импорта — перезагружаем inbox, чтобы новые связанные
                  // записи попали в список обновления.
                  onApplied();
                  const restrictIds = isRestricted ? restrictKey.split(',') : undefined;
                  if (kind === 'fields') {
                    fetchSyncInbox(spaceId, viewId, direction, { includeFieldSlugs, itemIds: restrictIds }).then(setInbox).catch(() => {});
                  } else {
                    fetchLinksInbox(spaceId, viewId, direction, { includeLinkTypes, itemIds: restrictIds }).then(setLinksInbox).catch(() => {});
                  }
                }}
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Импорт новых задач из Jira доступен для представлений с одним типом записей.
              </Text>
            )}
          </div>
        )}

        {/* Body */}
        {phase === 'loading' && <div style={{ padding: 40, textAlign: 'center' }}>Загрузка inbox…</div>}
        {phase === 'error' && (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Text type="danger">{errorMsg}</Text>
          </div>
        )}
        {phase === 'ready' && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="show-only-actionable"
              checked={showOnlyActionable}
              onChange={(e) => setShowOnlyActionable(e.target.checked)}
            />
            <label htmlFor="show-only-actionable" style={{ fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
              Показать только записи с изменениями
            </label>
          </div>
        )}
        {phase === 'ready' && kind === 'fields' && inbox && (
          <Table<SyncInboxItem>
            rowKey="itemId"
            dataSource={showOnlyActionable
              ? inbox.items.filter((i) => i.action === 'create' || i.action === 'update' || !!i.errorMessage)
              : inbox.items}
            columns={columns}
            size="small"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            rowSelection={{
              selectedRowKeys: selectedItemIds,
              onChange: (keys) => setSelectedItemIds(keys as string[]),
              getCheckboxProps: (row) => ({
                disabled: row.action === 'no_change' || row.action === 'unlinked' || !!row.errorMessage,
              }),
            }}
          />
        )}
        {phase === 'ready' && kind === 'links' && linksInbox && (
          <Table<LinksInboxItem>
            rowKey="itemId"
            dataSource={showOnlyActionable
              ? linksInbox.items.filter((i) => i.actionableCount > 0 || !!i.errorMessage)
              : linksInbox.items}
            columns={linksColumns}
            size="small"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            rowSelection={{
              selectedRowKeys: selectedItemIds,
              onChange: (keys) => setSelectedItemIds(keys as string[]),
              getCheckboxProps: (row) => ({
                disabled: row.actionableCount === 0 || !!row.errorMessage,
              }),
            }}
          />
        )}
        {phase === 'ready' && kind === 'both' && (
          <Table<BothRow>
            rowKey="itemId"
            dataSource={showOnlyActionable
              ? bothRows.filter((r) =>
                  r.fieldAction === 'create' || r.fieldAction === 'update' || r.linkActionable > 0 || r.hasError)
              : bothRows}
            columns={bothColumns}
            size="small"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            rowSelection={{
              selectedRowKeys: selectedItemIds,
              onChange: (keys) => setSelectedItemIds(keys as string[]),
              getCheckboxProps: (row) => ({
                disabled: (
                  (row.fieldAction !== 'create' && row.fieldAction !== 'update') &&
                  row.linkActionable === 0
                ) || row.hasError,
              }),
            }}
          />
        )}
        {(isRunning || isTerminal) && (
          <div style={{ padding: 24 }}>
            <Progress
              percent={progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}
              status={phase === 'error' ? 'exception' : phase === 'success' ? 'success' : 'active'}
            />
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              {phase === 'starting' && 'Запуск…'}
              {phase === 'running' && `Обработано ${progress.done} из ${progress.total}`}
              {phase === 'success' && result && (
                <>
                  <Text>
                    Готово: <b>{result.ok}</b> {direction === 'push' ? 'запушено' : 'обновлено'}
                    {result.errors > 0 && <>, <Text type="danger"><b>{result.errors}</b> с ошибками</Text></>}
                  </Text>
                  {result.errorDetails.length > 0 && (
                    <div style={{ marginTop: 12, textAlign: 'left' }}>
                      <Collapse
                        size="small"
                        defaultActiveKey={['errors']}
                        items={[{
                          key: 'errors',
                          label: <Text type="danger">Ошибки ({result.errorDetails.length})</Text>,
                          children: (
                            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 240, overflow: 'auto' }}>
                              {result.errorDetails.map((e, idx) => (
                                <li key={`${e.itemId}-${idx}`} style={{ marginBottom: 6 }}>
                                  {e.jiraIssueKey && <Tag>{e.jiraIssueKey}</Tag>}
                                  <Text strong>{e.title}</Text>
                                  <div><Text type="danger" style={{ fontSize: 12 }}>{e.error}</Text></div>
                                </li>
                              ))}
                            </ul>
                          ),
                        }]}
                      />
                    </div>
                  )}
                </>
              )}
              {phase === 'error' && <Text type="danger">{errorMsg}</Text>}
            </div>
          </div>
        )}
      </Modal>

      {drawerItemId && kind === 'fields' && (
        <SyncRecordDrawer
          open
          spaceId={spaceId}
          itemId={drawerItemId}
          direction={direction}
          includeFieldSlugs={includeFieldSlugs}
          onClose={() => setDrawerItemId(null)}
          onApplied={() => {
            setDrawerItemId(null);
            const restrictIds = isRestricted ? restrictKey.split(',') : undefined;
            fetchSyncInbox(spaceId, viewId, direction, { includeFieldSlugs, itemIds: restrictIds }).then(setInbox).catch(() => {});
          }}
        />
      )}
      {drawerItemId && kind === 'links' && (
        <LinksRecordDrawer
          open
          spaceId={spaceId}
          itemId={drawerItemId}
          direction={direction}
          includeLinkTypes={includeLinkTypes}
          onClose={() => setDrawerItemId(null)}
          onApplied={() => {
            setDrawerItemId(null);
            const restrictIds = isRestricted ? restrictKey.split(',') : undefined;
            fetchLinksInbox(spaceId, viewId, direction, { includeLinkTypes, itemIds: restrictIds }).then(setLinksInbox).catch(() => {});
          }}
        />
      )}
    </>
  );
}
